import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/api';
import "./AdminDashboard.css";
import GradientHeading from './GradientHeading';
import { useTheme } from '../context/ThemeContext';

// A FastAPI validation error (422) returns `detail` as an ARRAY of
// {type, loc, msg, input} objects, not a string -- rendering that directly
// as JSX crashes React ("Objects are not valid as a React child"). Every
// other error shape here is already a plain string. Always reduce to one.
const extractErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ') || fallback;
  }
  return fallback;
};

const AdminDashboard = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  // Client-login modal state (2026-09-16, internal-only; extended 2026-09-17 for multi-shop
  // clients -- one email can now be granted access to several shops in one submission, since
  // the backend authorizes a real list (client_shop_ids), not a single shop).
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const generateClientPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    const bytes = new Uint32Array(14);
    window.crypto.getRandomValues(bytes);
    setClientPassword(Array.from(bytes, (b) => chars[b % chars.length]).join(''));
  };
  const [clientSelectedShopIds, setClientSelectedShopIds] = useState([]);
  const [clientCreating, setClientCreating] = useState(false);
  const [clientResult, setClientResult] = useState(null);
  // Shown on the page itself (not inside the modal) after a successful save, since the
  // modal closes immediately on success and the confirmation still needs to be visible.
  const [pageNotice, setPageNotice] = useState(null);
  useTheme(); // ensure theme CSS variables are active

  const openClientModal = (preselectShop) => {
    setClientModalOpen(true);
    setClientEmail('');
    setClientPassword('');
    setClientSelectedShopIds(preselectShop ? [preselectShop.id] : []);
    setClientResult(null);
  };

  const closeClientModal = () => {
    setClientModalOpen(false);
    setClientResult(null);
  };

  const toggleShopSelection = (shopId) => {
    setClientSelectedShopIds((prev) =>
      prev.includes(shopId) ? prev.filter((id) => id !== shopId) : [...prev, shopId]
    );
  };

  const clientFormValid = clientEmail && clientPassword && clientSelectedShopIds.length > 0;

  const submitCreateClientLogin = async () => {
    if (!clientFormValid) {
      setClientResult({ ok: false, message: 'Email, password, and at least one shop are required.' });
      return;
    }
    setClientCreating(true);
    setClientResult(null);
    try {
      const resp = await apiClient.post('/internal/create-client-login', {
        shop_ids: clientSelectedShopIds,
        email: clientEmail,
        password: clientPassword,
      });
      const shopNames = resp.data.shops.map((s) => s.shop_name).join(', ');
      const verb = resp.data.created ? 'Created' : 'Updated';
      const emailNote = resp.data.email_sent ? ' Welcome email sent.' : ' (Welcome email failed to send — share credentials manually.)';
      // Close the modal and reset it, then surface the confirmation on the page itself for
      // a few seconds -- previously the modal stayed open with an active Save button after
      // a successful save, inviting an accidental duplicate click.
      setClientModalOpen(false);
      setClientEmail('');
      setClientPassword('');
      setClientSelectedShopIds([]);
      setClientResult(null);
      setPageNotice({ ok: true, message: `${verb} — ${resp.data.email} can now access: ${shopNames}.${emailNote}` });
      setTimeout(() => setPageNotice(null), 5000);
    } catch (err) {
      setClientResult({ ok: false, message: extractErrorMessage(err, 'Failed to create client login.') });
    } finally {
      setClientCreating(false);
    }
  };

  useEffect(() => {
    apiClient.get('/admin/all-shops')
      .then(response => {
        setShops(Array.isArray(response.data) ? response.data : []);
      })
      .catch(err => {
        console.error("Error fetching shops:", err);
        setError("Failed to load shops.");
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredShops = shops.filter(shop => {
    const q = search.toLowerCase();
    return (
      (shop.shop_name || '').toLowerCase().includes(q) ||
      (shop.tiktok_shop_id || '').toLowerCase().includes(q)
    );
  });

  const statusColor = (status) => {
    if (!status || status === 'unknown') return '#6b7280';
    const s = status.toLowerCase();
    if (s.includes('activ')) return '#10b981';
    if (s.includes('pend')) return '#f59e0b';
    return '#6b7280';
  };

  return (
    <div className="admin-dashboard modern-admin-dashboard">
      <div className="dashboard-header">
        <GradientHeading>
          <h1>Shops</h1>
        </GradientHeading>
        <div className="search-container" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by shop name or Shop ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-bar modern-search-bar"
          />
          <button
            type="button"
            className="view-shop-btn"
            onClick={() => openClientModal(null)}
            style={{ cursor: 'pointer', border: 'none', whiteSpace: 'nowrap' }}
          >
            <span role="img" aria-label="key">🔑</span> Create Client Login
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-dashboard-error">{error}</div>
      )}

      {pageNotice && (
        <div
          style={{
            margin: '0 0 16px', padding: '12px 16px', borderRadius: '8px',
            background: pageNotice.ok ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
            border: `1px solid ${pageNotice.ok ? '#10b981' : '#ef4444'}`,
            color: pageNotice.ok ? '#10b981' : '#ef4444', fontSize: '13px',
          }}
        >
          {pageNotice.message}
        </div>
      )}

      <div className="table-wrapper">
        <table className="admin-dashboard-table modern-table">
          <thead>
            <tr>
              <th>Shop Name</th>
              <th>Shop ID</th>
              <th>Status</th>
              <th>Scraper</th>
              <th>Last Scraped</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="no-data">Loading shops...</td>
              </tr>
            ) : filteredShops.length === 0 ? (
              <tr>
                <td colSpan={6} className="no-data">
                  {search ? 'No shops match your search.' : 'No shops found.'}
                </td>
              </tr>
            ) : (
              filteredShops.map((shop) => (
                <tr key={shop.id} className="user-row">
                  <td className="username-cell">{shop.shop_name}</td>
                  <td className="brand-cell">
                    {shop.tiktok_shop_id || '—'}
                  </td>
                  <td>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: statusColor(shop.activation_status),
                      textTransform: 'capitalize',
                    }}>
                      {shop.activation_status || 'unknown'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      color: shop.scraper_enabled ? 'var(--adm-scraper-on)' : 'var(--adm-scraper-off)',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}>
                      {shop.scraper_enabled ? '✓ On' : 'Off'}
                    </span>
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--adm-text-3)' }}>
                    {shop.last_scraped ? new Date(shop.last_scraped).toLocaleDateString() : '—'}
                  </td>
                  <td className="action-cell" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Link to={`/admin/shop/${shop.id}`} className="view-shop-btn">
                      <span role="img" aria-label="shop">🔎</span> View Shop
                    </Link>
                    <button
                      type="button"
                      className="view-shop-btn"
                      onClick={() => openClientModal(shop)}
                      style={{ cursor: 'pointer', border: 'none' }}
                    >
                      <span role="img" aria-label="key">🔑</span> Create Client Login
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {!loading && filteredShops.length > 0 && (
          <div className="table-count">
            {filteredShops.length} of {shops.length} shops
          </div>
        )}
      </div>

      {clientModalOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={closeClientModal}
        >
          <div
            style={{
              background: 'var(--adm-bg, #111827)', color: 'inherit', borderRadius: '12px',
              padding: '24px', width: '420px', maxWidth: '90vw', maxHeight: '80vh',
              display: 'flex', flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Create / Update Client Login</h3>
            <p style={{ fontSize: '13px', opacity: 0.7, marginTop: '-8px' }}>
              This account will only ever see the shop(s) checked below — nothing else on the platform.
              If the email already has a client login, checked shops are added to their existing access
              and the password is reset to what you enter here.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
              <input
                type="email"
                placeholder="Client email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                style={{ padding: '10px', borderRadius: '6px', border: '1px solid #374151', background: 'transparent', color: 'inherit' }}
              />
              <input
                type="text"
                placeholder="Password"
                value={clientPassword}
                onChange={(e) => setClientPassword(e.target.value)}
                style={{ padding: '10px', borderRadius: '6px', border: '1px solid #374151', background: 'transparent', color: 'inherit' }}
              />
              {/* RELEASED (2026-10-18, explicit instruction): no longer roland@-only -- this
                  page is already internal-admin-only, and the backend's forced-change flow now
                  applies to any internal caller's client logins, not just roland@'s. */}
              <button type="button" onClick={generateClientPassword} style={{ padding: '10px', borderRadius: '6px', border: '1px solid #374151', background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
                Generate password
              </button>
            </div>

            <div style={{ fontSize: '12px', opacity: 0.6, marginTop: '18px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Shops to grant access to
            </div>
            <div style={{ overflowY: 'auto', border: '1px solid #374151', borderRadius: '6px', padding: '8px', flex: 1, minHeight: '120px' }}>
              {shops.map((shop) => (
                <label key={shop.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px', cursor: 'pointer', fontSize: '13px' }}>
                  <input
                    type="checkbox"
                    checked={clientSelectedShopIds.includes(shop.id)}
                    onChange={() => toggleShopSelection(shop.id)}
                  />
                  {shop.shop_name}
                </label>
              ))}
            </div>

            {clientResult && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: clientResult.ok ? '#10b981' : '#ef4444' }}>
                {clientResult.message}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeClientModal} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #374151', background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
                Close
              </button>
              <button
                type="button"
                onClick={submitCreateClientLogin}
                disabled={clientCreating || !clientFormValid}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#10b981', color: '#fff', cursor: (clientCreating || !clientFormValid) ? 'not-allowed' : 'pointer', opacity: (clientCreating || !clientFormValid) ? 0.5 : 1 }}
              >
                {clientCreating ? 'Saving…' : `Save (${clientSelectedShopIds.length} shop${clientSelectedShopIds.length === 1 ? '' : 's'})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
